import React, { useState } from "react"
import { manageCatvDelete } from "../../../../api/manage_catv_delete"
import { ManageCatvData } from "../"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

interface ManageCatvDeleteProps {
  onClose: (num: number) => void // モーダルを閉じるためのコールバック
  catvData: ManageCatvData // 編集するサービス提供元データをプロパティとして受け取る
}

const ManageCatvDelete: React.FC<ManageCatvDeleteProps> = ({ onClose, catvData }) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const [formData] = useState({
    id: catvData.id,
    name: catvData.name,
    address: catvData.address,
    phone: catvData.phone,
    sensitivityEnabled: catvData.sensitivity_enabled === 1,
    sensitivity: catvData.sensitivity.toString(),
    detectionEnabled: catvData.detection_enabled === 1,
    detectionPeriodStart: catvData.detection_period[0] || "00:00", // 最初の期間をセット
    detectionPeriodEnd: catvData.detection_period[1] || "00:00", // 最後の期間をセット
    optionEnabled: catvData.option_enabled === 1,
    optionPeriodStart: catvData.option_period[0] || "00:00", // 最初のオプション期間
    optionPeriodEnd: catvData.option_period[1] || "00:00", // 最後のオプション期間
    criticalCountEnabled: catvData.critical_count_enabled === 1,
    criticalCount: catvData.critical_count.toString()
  })

  const [errors, setErrors] = useState({
    id: "",
    name: "",
    address: "",
    phone: "",
    sensitivity: "",
    detectionPeriodStart: "",
    detectionPeriodEnd: "",
    optionPeriodStart: "",
    optionPeriodEnd: "",
    criticalCount: "",
    server: ""
  })

  const convertEnabledValues = (data: any) => {
    return {
      ...data,
      sensitivityEnabled: data.sensitivityEnabled ? 1 : 0,
      detectionEnabled: data.detectionEnabled ? 1 : 0,
      optionEnabled: data.optionEnabled ? 1 : 0,
      criticalCountEnabled: data.criticalCountEnabled ? 1 : 0
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // バリデーション成功時の処理
    const validationErrors = { ...errors }
    validationErrors.server = ""
    setErrors(validationErrors)

    const convertedData = convertEnabledValues(formData)
    try {
      startLoading()
      await manageCatvDelete(convertedData.id)
      onClose(1)
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login") // セッション切れ時にトップページへリダイレクト
      } else if (error.response) {
        const errorDetail = error.response.data.detail
        const validationErrors = { ...errors }
        validationErrors.server = errorDetail.message
        setErrors(validationErrors)
      } else {
        const validationErrors = { ...errors }
        validationErrors.server = "サーバーへの接続に失敗しました"
        setErrors(validationErrors)
      }
    } finally {
      stopLoading()
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "8px 16px",
            boxSizing: "border-box"
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" style={{ flex: 1, textAlign: "left" }}>
            サービス提供元を削除
          </MDTypography>
          <button
            className="close-button"
            onClick={() => onClose(0)}
            style={{
              position: "absolute",
              right: 16,
              top: 8,
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </div>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="サービス提供元ID"
              type="text"
              name="id"
              value={formData.id}
              sx={{ width: "100%" }}
              inputProps={{ readOnly: true }}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="サービス提供元名"
              type="text"
              name="name"
              value={formData.name}
              sx={{ width: "100%" }}
              inputProps={{ readOnly: true }}
            />
          </MDBox>
          {errors.name && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.name}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="住所"
              type="text"
              name="address"
              value={formData.address}
              sx={{ width: "100%" }}
              inputProps={{ readOnly: true }}
            />
          </MDBox>
          {errors.address && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.address}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="電話番号"
              type="text"
              name="phone"
              value={formData.phone}
              sx={{ width: "100%" }}
              inputProps={{ readOnly: true }}
            />
          </MDBox>
          {errors.phone && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.phone}
            </MDBox>
          )}
          {errors.server && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.server}
            </MDBox>
          )}
          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              削除
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManageCatvDelete
